import { isPlatformServer, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { ActivityLocationDisplayComponent } from '@curbnturf/activity-component/src/lib/activity-location-display/activity-location-display.component';
import { ActivityLocationPhotoComponent } from '@curbnturf/activity-component/src/lib/activity-location-display/activity-location-photo/activity-location-photo.component';
import { ActivityFormModalComponent } from '@curbnturf/activity-component/src/lib/add-activity-form/activity-form-modal/activity-form-modal.component';
import { AddActivityFormComponent } from '@curbnturf/activity-component/src/lib/add-activity-form/add-activity-form.component';
import { LocationMapComponent } from '@curbnturf/activity-component/src/lib/location-map/location-map.component';
import { AuthActionTypes } from '@curbnturf/auth/src/lib/+state/auth.actions';
import { CategorySvgComponent } from '@curbnturf/custom-svgs/src/lib/category-svg/category-svg.component';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import {
  ActivityLocation,
  BoondockPoint,
  CurbnturfMapLayerTypes,
  DEFAULT_MAP_LATITUDE,
  DEFAULT_MAP_LONGITUDE,
  DEFAULT_MAP_ZOOM_LEVEL,
  HelperFunctions,
  IActivityLocation,
  IBoondockPoint,
  ICancellableDebounceObject,
  IFilterPanelOptions,
  ILatLon,
  IListingPoint,
  IMapPoint,
  IPoiFilterPanelOptions,
  IRoutePoint,
  IUserCustomLocation,
  LatLon,
  ListingPoint,
  MAP_CLUSTER_RANGE,
  MAP_CLUSTER_WEIGHT,
  MAP_INACCURACY_ZOOM_CUTOFF,
  MapLayerTypes,
  MapPointTypes,
} from '@curbnturf/entities';
import { containsRect, latLng2Coord, toLatLng, toLatLon } from '@curbnturf/helpers';
import { SaveFavoriteComponent } from '@curbnturf/listing-edit/src/lib/save-favorite/save-favorite.component';
import { ListingSearchFacade } from '@curbnturf/listing-search/src/lib/+state/listing-search.facade';
import { PlatformService } from '@curbnturf/network/src/lib/platform/platform.service';
import { ActivityCardComponent } from '@curbnturf/search-components/src/lib/activity-card/activity-card.component';
import { BoondockSearchDisplayCardComponent } from '@curbnturf/search-components/src/lib/boondock-search-display-card/boondock-search-display-card.component';
import { SearchDisplayCardComponent } from '@curbnturf/search-components/src/lib/search-display-card/search-display-card.component';
import { UserPointCardComponent } from '@curbnturf/search-components/src/lib/user-point-card/user-point-card.component';
import { SpinnerComponent } from '@curbnturf/ui/src/lib/spinner/spinner.component';
import { IonicModule, LoadingController, ModalController, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Feature, Point } from 'geojson';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { first, skipWhile } from 'rxjs/operators';
import { CircularTheme, IconMarkerTheme, multiPinCoordinates } from '../cluster';
import { IClusterData } from '../cluster-data.interface';
import { IDataItem } from '../data-item.interface';
import { HereMapContextMenuComponent } from '../here-map-context-menu/here-map-context-menu.component';
import { destinationIcon, intermediateIcon, originIcon } from '../icons';
import { IOnMapLayer } from '../on-map-layer.interface';
import { IViewportData } from '../viewport-data.interface';
import { flattenPoiDataItem, makeDataPoint } from './here-map.helpers';
import { HereMapService } from './here-map.service';
import buildActivityRoute = HelperFunctions.buildActivityRoute;
import buildListingPointRoute = HelperFunctions.buildListingPointRoute;
import buildBoondockingPointRoute = HelperFunctions.buildBoondockingPointRoute;
import debounce = HelperFunctions.debounce;
import debounceCancellable = HelperFunctions.debounceCancellable;

export const currentLocationPin = `<svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 30 41.998429" id="svg175" width="30" height="41.998428">
  <defs id="defs179" />
  <circle cx="15" cy="15" id="circle171" r="3" style="stroke-width:0.09375;fill:#e3530d;fill-opacity:1" />
  <path d="M 15,0 C 6.7293749,0 0,6.4359375 0,14.34375 0,18.109687 1.7165631,23.117812 5.1018749,29.229375 7.820625,34.13625 10.965937,38.573437 12.601876,40.78125 a 2.9765631,2.9765625 0 0 0 4.801872,0 c 1.633128,-2.207813 4.781252,-6.645 7.5,-11.551875 C 28.283438,23.119688 30,18.111562 30,14.34375 30,6.4359375 23.27063,0 15,0 Z m 0,21 a 6,6 0 1 1 6,-6 6.0065631,6.0065625 0 0 1 -6,6 z" id="path173" style="stroke-width:0.09375;fill:#e3530d;fill-opacity:1" />
</svg>`;

export const userPointPin = `<svg
   xmlns:dc="http://purl.org/dc/elements/1.1/"
   xmlns:cc="http://creativecommons.org/ns#"
   xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#"
   xmlns="http://www.w3.org/2000/svg"
   xmlns:sodipodi="http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd"
   xmlns:inkscape="http://www.inkscape.org/namespaces/inkscape"
   class="ionicon"
   viewBox="0 0 20 27.9989"
   id="svg175">
  <metadata
     id="metadata910">
    <rdf:RDF>
      <cc:Work
         rdf:about="">
        <dc:format>image/svg+xml</dc:format>
        <dc:type
           rdf:resource="http://purl.org/dc/dcmitype/StillImage" />
        <dc:title></dc:title>
      </cc:Work>
    </rdf:RDF>
  </metadata>
  <sodipodi:namedview
     pagecolor="#ffffff"
     bordercolor="#666666"
     borderopacity="1"
     objecttolerance="10"
     gridtolerance="10"
     guidetolerance="10"
     inkscape:pageopacity="0"
     inkscape:pageshadow="2"
     inkscape:window-width="1920"
     inkscape:window-height="949"
     id="namedview908"
     showgrid="false"
     inkscape:zoom="25.4"
     inkscape:cx="10"
     inkscape:cy="15.35309"
     inkscape:window-x="0"
     inkscape:window-y="25"
     inkscape:window-maximized="0"
     inkscape:current-layer="svg175" />
  <defs
     id="defs179" />
  <circle
     cx="15"
     cy="15"
     id="circle171"
     r="3"
     style="stroke-width:0.09375;fill:#e3530d;fill-opacity:1" />
  <path
     d="M 10,0 C 4.4862499,0 0,4.3001978 0,9.5838348 0,12.100061 1.1443754,15.44626 3.4012499,19.529726 5.21375,22.808274 7.3106247,25.772999 8.4012507,27.248158 a 1.9843754,1.9888023 0 0 0 3.2012483,0 c 1.088752,-1.475159 3.187501,-4.439884 5,-7.718432 C 18.855625,15.447513 20,12.101314 20,9.5838348 20,4.3001978 15.513753,0 10,0 Z m 0,14.031235 a 4,4.0089244 0 1 1 4,-4.008924 4.0043754,4.0133091 0 0 1 -4,4.008924 z"
     id="path173"
     style="fill:#e3530d;fill-opacity:1;stroke-width:0.0625697" />
</svg>
`;

@Component({
  standalone: true,
  selector: 'curbnturf-here-map',
  templateUrl: './here-map.component.html',
  styleUrls: ['./here-map.component.scss'],
  imports: [
    ActivityCardComponent,
    forwardRef(() => ActivityFormModalComponent),
    forwardRef(() => ActivityLocationDisplayComponent),
    ActivityLocationPhotoComponent,
    forwardRef(() => AddActivityFormComponent),
    CategorySvgComponent,
    forwardRef(() => HereMapContextMenuComponent),
    IonicModule,
    forwardRef(() => LocationMapComponent),
    NgFor,
    NgIf,
    SaveFavoriteComponent,
    SiteSvgsComponent,
    BoondockSearchDisplayCardComponent,
    SearchDisplayCardComponent,
    SpinnerComponent,
    UserPointCardComponent,
  ],
})
export class HereMapComponent implements OnChanges, OnDestroy, OnInit {
  @ViewChild('map') mapElement: ElementRef;

  @ViewChild('bubbleBoondockContent') bubbleBoondockContent: ElementRef;
  @ViewChild('bubbleContent') bubbleContent: ElementRef;

  @ViewChild('bubbleActivityContent') bubbleActivityContent: ElementRef;
  @ViewChild('bubbleUserPointContent') bubbleUserPointContent: ElementRef;

  @ViewChild('contextMenu') contextMenuContainer: ElementRef;

  @Input() lat: number;
  @Input() lon: number;

  @Input() width: string = '100%';

  @Input() height: string = '100%';
  @Input() mapBoondockPoints: IBoondockPoint[];
  @Input() mapHostPoints: IListingPoint[];
  @Input() pins: ILatLon[];
  @Input() mapPoiPoints: IActivityLocation[];
  @Input() mapUserPoints: IMapPoint[];
  @Input() mapFilters?: IFilterPanelOptions;
  @Input() mapActivityFilters?: IPoiFilterPanelOptions;

  @Input() addPoiMode: boolean = false;
  @Input() type?: 'normal' | 'satellite';
  @Input() zoom: number = DEFAULT_MAP_ZOOM_LEVEL;

  @Input() forceLayerUpdate: boolean = false;

  @Input() route: ILatLon[];
  @Input() routeCorridor: ILatLon[];
  @Input() routeWaypoints: IRoutePoint[];

  mapRoute: H.map.Polyline;
  mapCorridor: H.map.Polygon;

  @Input() showCurrentLocation: boolean = true;

  @Output() updatedViewport = new EventEmitter<IViewportData>();

  @Output() mapTapped = new EventEmitter<LatLon>();

  @Output() layersUpdated: EventEmitter<void> = new EventEmitter<void>();

  @Output() userPointAdded: EventEmitter<IUserCustomLocation> = new EventEmitter<IUserCustomLocation>();
  @Output() userPointUpdated: EventEmitter<IUserCustomLocation> = new EventEmitter<IUserCustomLocation>();
  @Output() userPointRemoved: EventEmitter<IUserCustomLocation> = new EventEmitter<IUserCustomLocation>();

  disabled: boolean = false;

  @Input() set disableControls(disable: boolean) {
    this.mapOnce$.subscribe(() => {
      if (disable) {
        this.ui?.removeControl('zoom');
      }
    });
  }

  @Input() draggable: boolean;
  @Output() drag = new EventEmitter<LatLon>();
  @Output() dragEnd = new EventEmitter<LatLon>();

  bubbleListing: ListingPoint;
  bubbleBoondock: BoondockPoint;
  currentPoint?: ListingPoint;
  currentBoondockPoint?: BoondockPoint;
  currentActivity?: ActivityLocation;
  currentUserPoint: IMapPoint;
  ui: H.ui.UI | null;

  contextMenuVisible = false;
  contextMenuCoordinates: ILatLon;

  private userPointBubble: H.ui.InfoBubble;
  private activityBubble: H.ui.InfoBubble;
  private boondockBubble: H.ui.InfoBubble;
  private listingBubble: H.ui.InfoBubble;
  private map: H.Map;
  private mapSubject: Subject<H.Map | undefined>;
  private mapOnce$: Observable<H.Map>;
  private mapReadySubject: Subject<H.Map | undefined>;
  private mapReadyOnce$: Observable<H.Map>;
  private behavior: H.mapevents.Behavior;
  private searchLocationMarker: H.map.Marker;
  private centerLat: number;
  private centerLon: number;

  private activePoiFilterChanged: boolean;
  private layers: {
    [id in CurbnturfMapLayerTypes]: IOnMapLayer;
  } = {
    host: { type: CurbnturfMapLayerTypes.host },
    hostInaccuracy: { type: CurbnturfMapLayerTypes.hostInaccuracy },
    boondock: { type: CurbnturfMapLayerTypes.boondock },
    poi: { type: CurbnturfMapLayerTypes.poi },
    currentLocation: { type: CurbnturfMapLayerTypes.currentLocation },
    route: { type: CurbnturfMapLayerTypes.route },
    // route and trip are separate as the route needs to be the bottom layer
    // and the trip layer needs to be the top layer
    trip: { type: CurbnturfMapLayerTypes.trip },
    user: { type: CurbnturfMapLayerTypes.user },
  };

  private prevSoutheast: LatLon;
  private prevNorthwest: LatLon;
  private prevCenter: LatLon;
  private prevZoom: number;
  private prevHeading: number;

  private readonly updateMapPinsDebounced: (pins: ILatLon[]) => Promise<void>;
  private readonly updateMapHostPointsDebounced: (points: IDataItem[]) => Promise<void>;
  private readonly updateMapBoondockPointsDebounced: (points: IDataItem[]) => Promise<void>;
  private readonly updateMapUserCustomPointsDebounced: (points: IDataItem[]) => Promise<void>;
  private readonly updateMapPoiPointsDebounced: () => Promise<void>;
  private readonly updateMapRouteDebounced: (
    waypoints: ILatLon[],
    corridor: ILatLon[],
    tripPoints: IRoutePoint[],
  ) => Promise<void>;
  private readonly removeMapRouteDebounced: () => ICancellableDebounceObject;
  private readonly mapResizeDebounced: () => Promise<void>;
  private readonly updateMapViewportDebounced: () => void;

  private subscriptions: Subscription[] = [];

  constructor(
    private actions$: Actions,
    private cd: ChangeDetectorRef,
    private hereMapService: HereMapService,
    private searchFacade: ListingSearchFacade,
    private loadingController: LoadingController,
    private modalController: ModalController,
    private navController: NavController,
    private platformService: PlatformService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    // access the map once
    this.mapSubject = new BehaviorSubject(undefined);
    this.mapOnce$ = this.mapSubject.pipe(
      skipWhile((map) => !map),
      first(),
    ) as Observable<H.Map>;

    // access the map once it is ready available
    this.mapReadySubject = new BehaviorSubject(undefined);
    this.mapReadyOnce$ = this.mapReadySubject.pipe(
      skipWhile((map) => !map),
      first(),
    ) as Observable<H.Map>;

    this.updateMapPinsDebounced = debounce(
      (pins) => this.updateMapPins(pins),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.updateMapHostPointsDebounced = debounce(
      (points) => this.updateHostPoints(points),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.updateMapBoondockPointsDebounced = debounce(
      (points) => this.updateBoondockPoints(points),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.updateMapUserCustomPointsDebounced = debounce(
      (points) => this.updateUserCustomPoints(points),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.updateMapPoiPointsDebounced = debounce(
      () => this.updateMapActivityPins(),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.updateMapRouteDebounced = debounce(
      (waypoints: ILatLon[], corridor: ILatLon[], tripPoints: IRoutePoint[]) =>
        this.updateMapRoute(waypoints, corridor, tripPoints),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.removeMapRouteDebounced = debounceCancellable(
      () => this.removeMapRoute(),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.mapResizeDebounced = debounce(
      () => {
        this.closeBubbles();
        this.mapOnce$.subscribe((map) => {
          map?.getViewPort().resize();
        });
      },
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.updateMapViewportDebounced = debounce(
      () => this.updateMapViewport(),
      isPlatformServer(this.platformId) ? 0 : 200,
    );

    this.platformService.resize.subscribe(async () => {
      await this.mapResizeDebounced();
    });

    ScreenOrientation.addListener('screenOrientationChange', async () => {
      await this.mapResizeDebounced();
    });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.searchFacade.selectedPoint$.subscribe((point) => {
        this.currentPoint = point;
        this.updateSelectedIcon(point);
        if (point) {
          if (!this.platformService.isPhoneSize()) {
            this.addBubble(point.id);
          }
        }
      }),
      this.searchFacade.selectedBoondockPoint$.subscribe((point) => {
        this.currentBoondockPoint = point;
        this.updateSelectedIcon(point);
        if (point) {
          if (!this.platformService.isPhoneSize()) {
            this.addBoondockBubble(point.id);
          }
        }
      }),
      this.searchFacade.mapType$.subscribe((type) => {
        this.setMapType(type);
      }),
      this.actions$.pipe(ofType(AuthActionTypes.LogoutSuccess)).subscribe(() => {
        this.clearUserPins();
        this.mapOnce$.subscribe(() => {
          setTimeout(() => {
            if (this.layers.trip.exclude) {
              this.layers.trip.exclude.boondock = [];
              this.layers.trip.exclude.poi = [];
              this.layers.trip.exclude.listing = [];
              this.layers.trip.exclude.user = [];
            }
            this.removeMapRoute();
          }, 10);
        });
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.pins) {
      this.updateMapPinsDebounced(changes.pins.currentValue);
    }

    if (changes.mapBoondockPoints && changes.mapBoondockPoints?.currentValue?.length > 0) {
      this.updateMapBoondockPointsDebounced(changes.mapBoondockPoints.currentValue);
    }

    if (changes.mapHostPoints && changes.mapHostPoints?.currentValue?.length > 0) {
      this.updateMapHostPointsDebounced(changes.mapHostPoints.currentValue);
    }

    if (changes.mapUserPoints && changes.mapUserPoints?.currentValue?.length > 0) {
      this.mapOnce$.subscribe(() => {
        this.updateMapUserCustomPointsDebounced(changes.mapUserPoints.currentValue);
      });
    }

    if (changes.mapActivityFilters) {
      this.activePoiFilterChanged = true;
    }

    if ((changes.mapPoiPoints && changes.mapPoiPoints?.currentValue?.length > 0) || changes.mapActivityFilters) {
      this.updateMapPoiPointsDebounced();
    }

    if (changes.mapFilters) {
      this.clearHostAndBoondockPins();
    }

    if (changes.route || changes.routeCorridor || changes.routeWaypoints) {
      this.activePoiFilterChanged = true;
      if (this.route && this.route.length >= 2 && this.routeCorridor) {
        // before updating the map route we should cancel any remove map debounces
        clearTimeout(this.removeMapRouteDebounced().timeout);

        this.updateMapRouteDebounced(this.route, this.routeCorridor, this.routeWaypoints);
      } else {
        if (this.layers.route.layer) {
          this.removeMapRouteDebounced();
        }
      }
    }
  }

  mapExists(): boolean {
    return this.map !== undefined;
  }

  buildMap() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.centerLat = this.centerLat || this.lat || DEFAULT_MAP_LATITUDE;
    this.centerLon = this.centerLon || this.lon || DEFAULT_MAP_LONGITUDE;

    const engineType = H.Map.EngineType['HARP'];
    const defaultLayers: any = this.hereMapService.platform.createDefaultLayers({ engineType });

    let layer = defaultLayers.vector.normal.map;
    if (this.type === 'normal') {
      layer = defaultLayers.vector.normal.map;
    } else if (this.type === 'satellite') {
      layer = defaultLayers.hybrid.day.raster;
    }

    layer?.setMin(5);

    this.map = new H.Map(this.mapElement.nativeElement, layer, {
      engineType,
      zoom: this.zoom,
      center: { lat: this.centerLat, lng: this.centerLon },
    });
    this.initializeLayers();

    const mapEvents = new H.mapevents.MapEvents(this.map);

    // Add event listeners:
    this.map.addEventListener('mapviewchangeend', () => this.updateMapViewportDebounced());
    this.map.addEventListener('tap', (event: Event) => this.handleMapTap(event));
    this.map.addEventListener('contextmenu', (event: Event) => this.handleContextMenu(event));
    this.map.addEventListener('dragstart', () => this.clearContextMenu());

    if (this.draggable) {
      this.map.addEventListener('dragend', (event: Event) => this.dragendAddDraggable(event), false);

      // Listen to the drag event and move the position of the marker
      // as necessary
      this.map.addEventListener('drag', (event: Event) => this.dragAddDraggable(event), false);
    }

    this.behavior = new H.mapevents.Behavior(mapEvents);

    // disable map controls with alt move mouse and gestures
    this.behavior.disable(H.mapevents.Behavior.Feature.TILT);

    // Create the default UI:
    this.ui = H.ui.UI.createDefault(this.map, defaultLayers);

    const mapSettings = this.ui?.getControl('mapsettings');

    mapSettings?.setAlignment(H.ui.LayoutAlignment.RIGHT_MIDDLE);

    // set the UI unit system to imperial measurement
    this.ui?.setUnitSystem(H.ui.UnitSystem.IMPERIAL);

    this.setCurrentLocationPin();
    this.mapSubject.next(this.map);

    // check for getZoom being set, seems the best bet so far
    const timeout = () =>
      setTimeout(() => {
        if (this.map.getZoom()) {
          setTimeout(() => this.mapReadySubject.next(this.map), 1000);
        } else {
          timeout();
        }
      }, 250);
    timeout();
  }

  clearMap() {
    this.mapOnce$.subscribe((map) => {
      map.dispose();
      this.behavior.dispose();
    });
  }

  /**
   * Resize the map to fit an altered viewport
   */
  resizeMap() {
    return this.mapResizeDebounced();
  }

  /**
   * Resets the map to a desired zoom level.
   * @param {number} zoomLevel - set the numeric zoom level.
   * @param {boolean} animate - animate the transition to desired zoom level.
   */
  setMapZoom(zoomLevel: number = 8, animate = false) {
    this.mapOnce$.subscribe((map) => {
      map.setZoom(zoomLevel, animate);
    });
  }

  setMapType(mapLayerType: MapLayerTypes) {
    // @todo incomplete method
  }

  /**
   * Resets the orientation to the desired heading.
   * @param {number} heading - set the numeric heading.
   */
  setMapHeading(heading: number = 180) {
    this.mapOnce$.subscribe((map) => {
      const viewPortData = map?.getViewModel()?.getLookAtData();
      map?.getViewModel()?.setLookAtData({ ...viewPortData, heading });
    });
  }

  /**
   * This sets the maps Current Location and potentially resets the map center.
   * @param {number} lat - current location latitude
   * @param {number} lon - current location longitude
   * @param {boolean} updateMapCenter -
   * @param {boolean | undefined} updateMapCenterIcon
   */
  setMapLocation(lat: number, lon: number, updateMapCenter: boolean = false, updateMapCenterIcon?: boolean) {
    this.centerLat = lat;
    this.centerLon = lon;
    this.mapOnce$.subscribe((map) => {
      if (updateMapCenter) {
        this.lat = lat;
        this.lon = lon;
        map.setCenter({ lat: this.centerLat, lng: this.centerLon });
        this.updateMapViewportDebounced();
      }

      if (updateMapCenterIcon) {
        this.setCurrentLocationPin();
      }
    });
  }

  /**
   * Update the center of the map to the specified location.
   * @param {number} lat - latitude of the desired location
   * @param {number} lon - longitude of the desired location.
   * @param {boolean} placeMarker - add a marker to the specified location
   */
  updateMapCenter(lat: number, lon: number, placeMarker: boolean = false) {
    const center = new LatLon({ lat, lon });
    if (!center.equals({ lat: this.centerLat, lon: this.centerLon })) {
      this.centerLat = lat;
      this.centerLon = lon;
      this.mapOnce$.subscribe((map) => {
        if (placeMarker) {
          if (this.searchLocationMarker) {
            try {
              map.removeObject(this.searchLocationMarker);
            } catch (e) {
              /* Ignore*/
            }
          }

          if (this.showCurrentLocation) {
            setTimeout(() => {
              this.searchLocationMarker = new H.map.Marker({ lat, lng: lon });
              map.addObject(this.searchLocationMarker);
            }, 1000);
          }
        }

        if (!placeMarker && this.layers.route.layer && this.mapCorridor) {
          map.getViewModel().setLookAtData({ bounds: this.mapCorridor.getBoundingBox() || undefined });
        } else {
          map.setCenter({ lat: this.centerLat, lng: this.centerLon });
          this.updateMapViewportDebounced();
        }
      });
    }
  }

  initializeLayers() {
    // add the route plate
    this.initializeRoutePlate(this.map);

    // add the route plate
    this.initializeHostInaccuracyPlate(this.map);

    // add the current location plate
    this.initializeCurrentLocationPlate(this.map);

    // add the poi plate
    this.initializePoiCluster(this.map);

    // add the boondock plate
    this.initializeBoondockCluster(this.map);

    // add the private listing plate
    this.initializeHostCluster(this.map);

    // add the user plate
    this.initializeUserPointsPlate(this.map);

    // add the trip plate
    this.initializeTripPlate(this.map);
  }

  private initializeCurrentLocationPlate(map: H.Map) {
    this.layers.currentLocation.provider = new H.map.provider.LocalObjectProvider();
    this.layers.currentLocation.layer = new H.map.layer.ObjectLayer(this.layers.currentLocation.provider);
    this.layers.currentLocation.layer?.setMax(100);
    map.addLayer(this.layers.currentLocation.layer);
  }

  private initializeHostInaccuracyPlate(map: H.Map) {
    this.layers.hostInaccuracy.provider = new H.map.provider.LocalObjectProvider();
    this.layers.hostInaccuracy.layer = new H.map.layer.ObjectLayer(this.layers.hostInaccuracy.provider);
    map.addLayer(this.layers.hostInaccuracy.layer);
  }

  private initializeTripPlate(map: H.Map) {
    this.layers.trip.provider = new H.map.provider.LocalObjectProvider();
    this.layers.trip.layer = new H.map.layer.ObjectLayer(this.layers.trip.provider);
    map.addLayer(this.layers.trip.layer);
  }

  private initializeRoutePlate(map: H.Map) {
    this.layers.route.provider = new H.map.provider.LocalObjectProvider();
    this.layers.route.layer = new H.map.layer.ObjectLayer(this.layers.route.provider);
    map.addLayer(this.layers.route.layer);
  }

  private initializeUserPointsPlate(map: H.Map) {
    this.layers.user.provider = new H.map.provider.LocalObjectProvider();
    this.layers.user.layer = new H.map.layer.ObjectLayer(this.layers.user.provider);
    map.addLayer(this.layers.user.layer);
  }

  removePlates() {
    // Can't add layers if the map doesn't exist yet.
    this.mapOnce$.subscribe((map) => {
      // Remove the route plate
      if (this.layers.route.layer) {
        map.removeLayer(this.layers.route.layer);
        this.layers.route.provider = undefined;
        this.layers.route.layer = undefined;
      }

      // Remove the current location plate
      if (this.layers.currentLocation.layer) {
        map.removeLayer(this.layers.currentLocation.layer);
        this.layers.currentLocation.provider = undefined;
        this.layers.currentLocation.layer = undefined;
      }

      // remove the poi plate
      if (this.layers.poi.layer) {
        this.layers.poi.clusterProvider
          ?.getRootGroup()
          .getObjects()
          .forEach((el) => {
            el.dispose();
          });
        map.removeLayer(this.layers.poi.layer);
        this.layers.poi.clusterProvider = undefined;
        this.layers.poi.layer = undefined;
      }

      // remove the boondock plate
      if (this.layers.boondock.layer) {
        this.layers.boondock.clusterProvider
          ?.getRootGroup()
          .getObjects()
          .forEach((el) => {
            el.dispose();
          });
        map.removeLayer(this.layers.boondock.layer);
        this.layers.boondock.clusterProvider = undefined;
        this.layers.boondock.layer = undefined;
      }

      // remove the private listing plate
      if (this.layers.host.layer) {
        this.layers.host.clusterProvider
          ?.getRootGroup()
          .getObjects()
          .forEach((el) => {
            el.dispose();
          });
        map.removeLayer(this.layers.host.layer);
        this.layers.host.clusterProvider = undefined;
        this.layers.host.layer = undefined;
      }

      // remove the private listing plate
      if (this.layers.hostInaccuracy.layer) {
        map.removeLayer(this.layers.hostInaccuracy.layer);
        this.layers.hostInaccuracy.provider = undefined;
        this.layers.hostInaccuracy.layer = undefined;
      }

      // remove the user plate
      if (this.layers.user.layer) {
        map.removeLayer(this.layers.user.layer);
        this.layers.user.provider = undefined;
        this.layers.user.layer = undefined;
      }

      // remove the trip plate
      if (this.layers.trip.layer) {
        map.removeLayer(this.layers.trip.layer);
        this.layers.trip.provider = undefined;
        this.layers.trip.layer = undefined;
      }
    });
  }

  updateMapViewport(): void {
    this.mapOnce$.subscribe((map) => {
      const viewPortData = map?.getViewModel()?.getLookAtData();
      const bounds = viewPortData?.bounds?.getBoundingBox();
      if (bounds) {
        const topLeft = bounds.getTopLeft();
        const bottomRight = bounds.getBottomRight();
        const zoom = map.getZoom();
        // if any of the corners are 0 then it is probably an error
        if (topLeft && bottomRight && topLeft.lat && topLeft.lng && bottomRight.lat && bottomRight.lng) {
          const northwest = new LatLon(latLng2Coord(topLeft));
          const southeast = new LatLon(latLng2Coord(bottomRight));
          const center = new LatLon(
            viewPortData.position
              ? latLng2Coord(viewPortData.position)
              : { lat: DEFAULT_MAP_LATITUDE, lon: DEFAULT_MAP_LONGITUDE },
          );
          const heading = viewPortData.heading || 180;

          if (this.prevZoom !== zoom) {
            const removalObjects = map.getObjects();
            // If there's nothing in the list then we don't need to remove anything
            if (removalObjects.length > 0) {
              // remove multi-pin markers
              map.removeObjects(removalObjects);
            }

            // hide host inaccuracy circles when zoomed out
            if ((!this.prevZoom || this.prevZoom >= MAP_INACCURACY_ZOOM_CUTOFF) && zoom < MAP_INACCURACY_ZOOM_CUTOFF) {
              this.layers.hostInaccuracy?.provider?.getRootGroup()?.setVisibility(false);
            } else if (
              (!this.prevZoom || this.prevZoom < MAP_INACCURACY_ZOOM_CUTOFF) &&
              zoom >= MAP_INACCURACY_ZOOM_CUTOFF
            ) {
              this.layers.hostInaccuracy?.provider?.getRootGroup()?.setVisibility(true);
            }
          }

          if (
            !this.prevNorthwest ||
            !this.prevSoutheast ||
            !containsRect({ northwest: this.prevNorthwest, southeast: this.prevSoutheast }, { northwest, southeast }) ||
            this.prevZoom !== zoom ||
            !center.equals(this.prevCenter) ||
            this.prevHeading !== heading
          ) {
            const distance = Math.max(
              center.distanceTo(new LatLon(southeast)),
              center.distanceTo(new LatLon({ lat: northwest.lat, lon: southeast.lon })),
              center.distanceTo(new LatLon(northwest)),
              center.distanceTo(new LatLon({ lat: southeast.lat, lon: northwest.lon })),
            );

            this.prevSoutheast = southeast;
            this.prevNorthwest = northwest;
            this.prevCenter = center;
            this.prevZoom = zoom;
            this.prevHeading = heading;
            this.centerLat = center.lat;
            this.centerLon = center.lon;

            this.updatedViewport.emit({
              southeast,
              northwest,
              center,
              zoom,
              distance,
              heading,
            });
          }
        }
      }
    });
  }

  updateMapPins(pins: ILatLon[]) {
    const markers = pins.map((pin) => new H.map.Marker(toLatLng(pin)));

    this.mapReadyOnce$.subscribe((map) => {
      map.removeObjects(
        map.getObjects().reduce((objects: H.map.Object[], object: H.map.Object) => {
          if (object) {
            objects.push(object);
          }

          return objects;
        }, [] as H.map.Object[]),
      );
      map.addObjects(markers);
    });
  }

  clearHostAndBoondockPins() {
    this.layers.host.clusterProvider?.setDataPoints([]);
    this.layers.boondock.clusterProvider?.setDataPoints([]);
  }

  clearActivityPins() {
    this.layers.poi.clusterProvider?.setDataPoints([]);
  }

  clearUserPins() {
    this.layers.user.clusterProvider?.setDataPoints([]);
  }

  updateHostPoints(points: IListingPoint[]) {
    this.mapOnce$.subscribe(() => {
      const now = DateTime.now().toSeconds();
      this.layers.host.clusterPoints =
        this.layers.host.clusterPoints?.filter((point) => point.data?.expires >= now) || [];
      const existingPoints = [...this.layers.host.clusterPoints];
      const cachedPointIds = existingPoints.map((point) => point.data?.id);

      let layerReplaceData = true;
      if (points.length < 500 && !this.forceLayerUpdate) {
        layerReplaceData = false;
      }

      if (!this.layers.host.clusterProvider) {
        return;
      }

      points.forEach((point: IListingPoint) => {
        const listingData = this.makeListingDataItem(point);
        const dataPoint = makeDataPoint(listingData, MapPointTypes.standard);

        if (!cachedPointIds.includes(point.id)) {
          // don't add the point if it should be excluded, but update excluded
          if ((point?.id || point?.id === 0) && this.layers.trip.exclude?.listing[point.id]) {
            this.layers.trip.exclude.listing[point.id] = point;
            return;
          }

          this.layers.host.clusterPoints?.push(dataPoint);
          if (!layerReplaceData) {
            this.layers.host.clusterProvider?.addDataPoint(dataPoint);
          }
        } else {
          if (this.layers.host.clusterPoints && this.layers.host.clusterProvider) {
            const foundCachedIndex = this.layers.host.clusterPoints.findIndex(
              (cachedPoint) => cachedPoint.data?.id === point.id,
            );

            if (foundCachedIndex >= 0 && this.layers.host.clusterPoints[foundCachedIndex].data?.price !== point.price) {
              const originalPoint = this.layers.host.clusterPoints[foundCachedIndex];
              this.layers.host.clusterPoints[foundCachedIndex] = dataPoint;
              if (!layerReplaceData) {
                this.layers.host.clusterProvider?.removeDataPoint(originalPoint);
                this.layers.host.clusterProvider?.addDataPoint(dataPoint);
              }
            }
          }
        }
      });

      if (this.forceLayerUpdate) {
        this.removePlates();
        this.initializeLayers();
        this.layersUpdated.emit();
      }

      if (layerReplaceData) {
        this.layers.host.clusterProvider?.setDataPoints(this.layers.host.clusterPoints);
      }

      this.loadingController.getTop().then((top) => {
        if (top) {
          this.loadingController.dismiss();
        }
      });
    });
  }

  updateBoondockPoints(points: IBoondockPoint[]) {
    this.mapOnce$.subscribe(() => {
      const now = DateTime.now().toSeconds();
      this.layers.boondock.clusterPoints =
        this.layers.boondock.clusterPoints?.filter((point) => point.data?.expires >= now) || [];
      const existingPoints = [...this.layers.boondock.clusterPoints];
      const cachedPointIds = existingPoints.map((point) => point.data?.id);

      let layerReplaceData = true;
      if (points.length < 500 && !this.forceLayerUpdate) {
        layerReplaceData = false;
      }

      if (!this.layers.boondock.clusterProvider) {
        return;
      }

      points.forEach((point: IBoondockPoint) => {
        const boondockData = this.makeBoondockDataItem(point);
        const dataPoint = makeDataPoint(boondockData, MapPointTypes.boondock);

        if (!cachedPointIds.includes(point.id)) {
          // don't add the point if it should be excluded, but update excluded
          if ((point?.id || point?.id === 0) && this.layers.trip.exclude?.boondock[point.id]) {
            this.layers.trip.exclude.boondock[point.id] = point;
            return;
          }

          this.layers.boondock.clusterPoints?.push(dataPoint);
          if (!layerReplaceData) {
            this.layers.boondock.clusterProvider?.addDataPoint(dataPoint);
          }
        } else {
          if (!this.layers.boondock.clusterPoints) {
            this.layers.boondock.clusterPoints = [];
          }
          const foundCachedIndex = this.layers.boondock.clusterPoints.findIndex(
            (cachedPoint) => cachedPoint.data?.id === point.id,
          );

          if (
            foundCachedIndex >= 0 &&
            this.layers.boondock.clusterPoints[foundCachedIndex].data?.price !== point.price
          ) {
            const originalPoint = this.layers.boondock.clusterPoints[foundCachedIndex];
            this.layers.boondock.clusterPoints[foundCachedIndex] = dataPoint;
            if (!layerReplaceData) {
              this.layers.boondock.clusterProvider?.removeDataPoint(originalPoint);
              this.layers.boondock.clusterProvider?.addDataPoint(dataPoint);
            }
          }
        }
      });

      if (this.forceLayerUpdate) {
        this.removePlates();
        this.initializeLayers();
        this.layersUpdated.emit();
      }

      if (layerReplaceData) {
        this.layers.boondock.clusterProvider?.setDataPoints(this.layers.boondock.clusterPoints);
      }

      this.loadingController.getTop().then((top) => {
        if (top) {
          this.loadingController.dismiss();
        }
      });
    });
  }

  updateMapActivityPins() {
    this.mapReadyOnce$.subscribe((map) => {
      // remove duplicate pois
      const poiHash = this.mapPoiPoints.reduce(
        (hash, point) => {
          if (point.id) {
            hash[point.id] = point;
          }

          return hash;
        },
        {} as { [id: number]: IActivityLocation },
      );
      const pois = Object.keys(poiHash).map((id) => poiHash[parseInt(id, 10)]);

      const poiPoints = flattenPoiDataItem(pois).map((item: IDataItem) => makeDataPoint(item, MapPointTypes.poi));

      // remove the markers from the multi-pin if there are some that are no longer there
      // todo: not sure if it is worth the effort but it would be nice to update the open cluster if changed instead but no idea how to implement
      const mapObjects = map.getObjects();
      let resetMultiPin = false;
      mapObjects.forEach((mapObject: H.map.Object) => {
        if (mapObject instanceof H.map.Marker) {
          const mapObjectData = mapObject.getData();
          const mapObjectId = mapObjectData?.id;
          const mapObjectPoiType = mapObjectData?.poiType;
          if (
            mapObjectId &&
            !poiPoints.find((point) => mapObjectId === point?.data?.id && mapObjectPoiType === point?.data?.poiType)
          ) {
            resetMultiPin = true;
          }
        }
      });

      if (resetMultiPin) {
        map.removeObjects(mapObjects);
      }

      if (this.activePoiFilterChanged) {
        this.activePoiFilterChanged = false;
        const activityPoints: H.clustering.DataPoint[] = [];
        poiPoints.forEach((poi) => {
          if (!poi?.data?.id) {
            return;
          }

          // don't add the point if it should be excluded, but update excluded
          if (this.layers.trip.exclude?.poi[poi.data.id]) {
            if (!this.layers.trip.exclude.poi[poi.data.id].find((point) => point.data?.poiType === poi.data.poiType)) {
              this.layers.trip.exclude.poi[poi.data.id].push(poi);
            }
            return;
          }

          activityPoints.push(poi);
        });

        this.layers.poi.clusterPoints = [...activityPoints];
        this.layers.poi.clusterProvider?.setDataPoints([...activityPoints]);
      } else {
        const newPois = poiPoints.filter(
          (newPoint) =>
            !this.layers?.poi?.clusterPoints ||
            !this.layers.poi.clusterPoints.find(
              (point) => newPoint?.data?.id === point?.data?.id && newPoint?.data?.poiType === point?.data?.poiType,
            ),
        );

        const poiAdd: H.clustering.DataPoint[] = [];
        newPois.forEach((poi) => {
          // don't add the point if it should be excluded, but update excluded
          if (this.layers.trip.exclude?.poi[poi.data?.id]) {
            if (!this.layers.trip.exclude.poi[poi.data.id].find((point) => point.data?.poiType === poi.data.poiType)) {
              this.layers.trip.exclude.poi[poi.data.id].push(poi);
            }
            return;
          }

          return poiAdd.push(poi);
        });

        this.layers.poi.clusterPoints?.push(...poiAdd);
        this.layers.poi.clusterProvider?.addDataPoints(poiAdd);
      }

      if (this.routeWaypoints?.length > 1) {
        this.addAndRemoveReplacedPins(false);
      }
    });
  }

  updateUserCustomPoints(points: IMapPoint[]) {
    this.mapOnce$.subscribe(() => {
      if (this.forceLayerUpdate) {
        this.removePlates();
        this.initializeLayers();
        this.layersUpdated.emit();
      }

      // Clear the layer to allow for updates
      this.layers.user.provider?.getRootGroup()?.removeAll();

      points.forEach((item: IMapPoint) => {
        // don't add the point if it should be excluded, but update excluded
        if (item.id && this.layers.trip.exclude?.user[item.id]) {
          this.layers.trip.exclude.user[item.id] = item;
          return;
        }

        this.buildAndAddUserPoint(item);
      });

      this.loadingController.getTop().then((top) => {
        if (top) {
          this.loadingController.dismiss();
        }
      });
    });
  }

  updateMapRoute(waypoints: ILatLon[], corridor: ILatLon[], tripPoints?: IRoutePoint[]) {
    this.mapOnce$.subscribe((map) => {
      if (map) {
        const linestring = new H.geo.LineString();
        waypoints.forEach((point) => linestring.pushPoint(toLatLng(point)));

        const complexPolyLineString = new H.geo.LineString();

        corridor.forEach((point) => complexPolyLineString.pushPoint(toLatLng(point)));

        // initialize a polyline with the linestring:
        this.mapRoute = new H.map.Polyline(linestring, {
          style: { lineWidth: 5 },
          data: null,
        });

        // create the corridor - the buffer area around the route where we look for things
        this.mapCorridor = new H.map.Polygon(complexPolyLineString, {
          style: { lineWidth: 0 },
          data: null,
        });

        // adding all the objects to a layer to allow ordering the layers so the route can be behind the markers
        if (!this.layers.route.provider) {
          this.initializeRoutePlate(map);
        }

        this.layers.route.provider?.getRootGroup()?.removeAll();
        this.layers.route.provider?.getRootGroup()?.addObject(this.mapRoute);
        this.layers.route.provider?.getRootGroup()?.addObject(this.mapCorridor);

        if (tripPoints && tripPoints.length) {
          this.updateTripLayer(tripPoints);
        }

        this.loadingController.getTop().then((top) => {
          if (top) {
            this.loadingController.dismiss();
          }
        });
      }
    });
  }

  updateTripLayer(tripPoints: IRoutePoint[]) {
    const markers: H.map.Marker[] =
      tripPoints.length < 2
        ? []
        : tripPoints.reduce((markers, item, index) => {
            if (item.isActive) {
              const point = item.listing || item.boondock || item.poi || item.user;
              const lat = point?.coordinates?.lat;
              const lon = point?.coordinates?.lon;

              let icon: H.map.Icon;
              if (index === 0) {
                icon = new H.map.Icon(originIcon());
              } else if (index === tripPoints.length - 1) {
                icon = new H.map.Icon(destinationIcon());
              } else {
                icon = new H.map.Icon(intermediateIcon(index), {
                  anchor: { x: 12, y: 12 },
                });
              }

              if (lat && lon) {
                const marker = new H.map.Marker({ lat, lng: lon }, { icon, data: null });

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                marker.addEventListener('tap', (event: any) => {
                  if (this.platformService.isPhoneSize()) {
                    if (item.isPoi && item.poi?.id) {
                      this.poiPhoneAction(item.poi.id);
                    } else if (item.listing?.id) {
                      this.hostPhoneAction(item.listing?.id);
                    } else if (item.boondock?.id) {
                      this.boondockPhoneAction(item.boondock?.id);
                    }
                  } else {
                    this.centerIfOnEdge(event.currentPointer);
                    if (item.isPoi && item.poi?.id) {
                      this.poiNonPhoneAction(item.poi.id, { lat, lon });
                    } else if (item.listing?.id) {
                      this.hostNonPhoneAction(item.listing?.id);
                    } else if (item.boondock?.id) {
                      this.boondockNonPhoneAction(item.boondock?.id);
                    }
                  }
                });

                markers.push(marker);
              }
            }
            return markers;
          }, [] as H.map.Marker[]);

    if (!this.layers.trip.provider) {
      this.initializeTripPlate(this.map);
    }

    this.layers.trip.provider?.getRootGroup()?.removeAll();
    this.layers.trip.provider?.getRootGroup()?.addObjects(markers);

    this.addAndRemoveReplacedPins(tripPoints.length < 2);
  }

  addAndRemoveReplacedPins(addAllWaypointsBack: boolean = false) {
    if (!this.layers.trip.exclude) {
      this.layers.trip.exclude = { listing: {}, boondock: {}, poi: {}, user: {} };
    }

    const excludedBoondockIds = Object.keys(this.layers.trip.exclude.boondock).map((id) => parseInt(id));
    const excludedPoiIds = Object.keys(this.layers.trip.exclude.poi).map((id) => parseInt(id));
    const excludedListingIds = Object.keys(this.layers.trip.exclude.listing).map((id) => parseInt(id));
    const excludedUserPointIds = Object.keys(this.layers.trip.exclude.user).map((id) => parseInt(id));

    const { waypointListingIds, waypointBoondockIds, waypointPoiIds, waypointUserPointIds } =
      this.routeWaypoints?.reduce(
        (waypointIds, waypoint) => {
          if (!waypoint.isActive) {
            return waypointIds;
          }

          if (waypoint.isListing) {
            if (waypoint.listing?.id) {
              waypointIds.waypointListingIds.push(waypoint.listing.id);
            }
          }

          if (waypoint.isBoondock) {
            if (waypoint.boondock?.id) {
              waypointIds.waypointBoondockIds.push(waypoint.boondock.id);
            }
          }

          if (waypoint.isPoi) {
            if (waypoint.poi?.id) {
              waypointIds.waypointPoiIds.push(waypoint.poi.id);
            }
          }

          if (waypoint.isUser && waypoint.user?.id) {
            waypointIds.waypointUserPointIds.push(waypoint.user.id);
          }

          return waypointIds;
        },
        { waypointListingIds: [], waypointBoondockIds: [], waypointPoiIds: [], waypointUserPointIds: [] } as {
          waypointListingIds: number[];
          waypointBoondockIds: number[];
          waypointPoiIds: number[];
          waypointUserPointIds: number[];
        },
      );

    if (addAllWaypointsBack) {
      waypointListingIds.length = 0;
      waypointBoondockIds.length = 0;
      waypointPoiIds.length = 0;
      waypointUserPointIds.length = 0;
    }

    const listingAddIds = excludedListingIds.filter((id) => !waypointListingIds.includes(id));
    const boondockAddIds = excludedBoondockIds.filter((id) => !waypointBoondockIds.includes(id));
    const poiAddIds = excludedPoiIds.filter((id) => !waypointPoiIds.includes(id));
    const userPointAddIds = excludedUserPointIds.filter((id) => !waypointUserPointIds.includes(id));

    if (listingAddIds.length) {
      listingAddIds
        .map((id) => this.layers.trip.exclude?.listing[id])
        .forEach((point: IListingPoint) => {
          if (point) {
            const listingData = makeDataPoint(this.makeListingDataItem(point), MapPointTypes.standard);

            this.layers.host.clusterProvider?.addDataPoint(listingData);
            this.layers.host.clusterPoints?.push(listingData);

            if (point.id) {
              delete this.layers.trip.exclude?.listing[point.id];
            }
          }
        });
    }

    if (boondockAddIds.length) {
      boondockAddIds
        .map((id) => this.layers.trip.exclude?.boondock[id])
        .forEach((point: IBoondockPoint) => {
          if (point) {
            const boondockData = makeDataPoint(this.makeBoondockDataItem(point), MapPointTypes.boondock);

            this.layers.boondock.clusterProvider?.addDataPoint(boondockData);
            this.layers.boondock.clusterPoints?.push(boondockData);

            if (point.id) {
              delete this.layers.trip.exclude?.boondock[point.id];
            }
          }
        });
    }

    if (poiAddIds.length) {
      const flattenedPois: H.clustering.DataPoint[] = poiAddIds.reduce((pois, id) => {
        if (this.layers.trip.exclude?.poi[id]) {
          pois.push(...this.layers.trip.exclude.poi[id]);
        }

        return pois;
      }, [] as H.clustering.DataPoint[]);
      flattenedPois.forEach((poi) => {
        if (poi) {
          if (
            !this.layers.poi.clusterPoints?.find(
              (point) => poi?.data?.id === point?.data?.id && poi?.data?.poiType === point?.data?.poiType,
            )
          ) {
            this.layers.poi.clusterProvider?.addDataPoint(poi);
            this.layers.poi.clusterPoints?.push(poi);
          }

          if (poi.data.id && this.layers.trip.exclude?.poi[poi.data.id]) {
            delete this.layers.trip.exclude?.poi[poi.data.id];
          }
        }
      });
    }

    if (userPointAddIds.length) {
      userPointAddIds
        .map((id) => this.layers.trip.exclude?.user[id])
        .forEach((userPoint: IMapPoint | undefined) => {
          if (userPoint && userPoint.id) {
            this.buildAndAddUserPoint(userPoint);
          }
        });
    }

    if (!addAllWaypointsBack) {
      // if something was added to the trip close multi-pin
      const removalObjects = this.map.getObjects();
      if (removalObjects.length) {
        this.map?.removeObjects(removalObjects);
      }

      this.routeWaypoints.forEach((waypoint) => {
        if (waypoint.isActive) {
          if (waypoint.isListing) {
            const listing = waypoint.listing;
            if (listing) {
              if (this.layers.boondock.clusterPoints) {
                const index = this.layers.boondock.clusterPoints.findIndex((point) => listing?.id === point.data?.id);

                if (index >= 0) {
                  this.layers.boondock.clusterProvider?.removeDataPoint(this.layers.boondock.clusterPoints[index]);

                  this.layers.boondock.clusterPoints.splice(index, 1);
                }
              }
            }

            if (this.layers.trip.exclude?.listing && listing) {
              if (listing.id) {
                this.layers.trip.exclude.listing[listing.id] = listing;
              }
            }
          } else if (waypoint.isBoondock && waypoint.boondock) {
            const boondock = waypoint.boondock;
            if (boondock) {
              if (this.layers.boondock.clusterPoints) {
                const index = this.layers.boondock.clusterPoints.findIndex((point) => boondock?.id === point.data?.id);

                if (index >= 0) {
                  this.layers.boondock.clusterProvider?.removeDataPoint(this.layers.boondock.clusterPoints[index]);

                  this.layers.boondock.clusterPoints.splice(index, 1);
                }
              }
            }

            if (this.layers.trip.exclude?.boondock && boondock) {
              if (boondock.id) {
                this.layers.trip.exclude.boondock[boondock.id] = boondock;
              }
            }
          } else if (waypoint.isPoi && waypoint.poi) {
            const poi = waypoint.poi;

            if (this.layers.poi.clusterPoints) {
              let index: number;
              do {
                index = this.layers.poi.clusterPoints?.findIndex((point) => poi?.id === point.data?.id);

                if (index >= 0) {
                  const subPoi = this.layers.poi.clusterPoints[index];
                  this.layers.poi.clusterProvider?.removeDataPoint(subPoi);
                  this.layers.poi.clusterPoints.splice(index, 1);
                  if (this.layers.trip.exclude?.poi && poi.id) {
                    if (!this.layers.trip.exclude.poi[poi.id]) {
                      this.layers.trip.exclude.poi[poi.id] = [];
                    }

                    this.layers.trip.exclude.poi[poi.id].push(subPoi);
                  }
                }
              } while (index >= 0);
            }
          } else if (waypoint.isUser && waypoint.user) {
            const userPoint = waypoint.user;
            if (userPoint.id) {
              if (this.layers.user.provider?.getRootGroup().getObjects()) {
                const index = this.layers.user.provider
                  .getRootGroup()
                  .getObjects()
                  .find((point) => userPoint?.id === point.getData()?.id);

                if (index) {
                  this.layers.user.provider?.getRootGroup()?.removeObject(index);
                }
              }

              if (this.layers.trip.exclude?.user) {
                this.layers.trip.exclude.user[userPoint.id] = userPoint;
              }
            }
          }
        }
      });
    }
  }

  removeMapRoute() {
    this.layers.route.provider?.getRootGroup()?.removeAll();
    this.layers.trip.provider?.getRootGroup()?.removeAll();

    this.addAndRemoveReplacedPins(true);
  }

  /**
   * Bubble Methods
   */
  closeBubbles() {
    this.ui?.getBubbles()?.forEach((bubble: H.ui.InfoBubble) => bubble.close());
  }

  addBubble(ev: number, coordinates?: ILatLon) {
    if (ev !== undefined) {
      const listing = this.mapHostPoints?.find((el) => el.id === ev);
      if (listing) {
        this.bubbleListing = new ListingPoint(listing);
        this.closeBubbles();
        this.cd.detectChanges();

        let content;
        if (this.listingBubble?.getContentElement()) {
          content = this.listingBubble.getContentElement();
        } else {
          content = this.bubbleContent.nativeElement.children[0];
        }

        this.listingBubble = new H.ui.InfoBubble({
          lat: coordinates?.lat || this.bubbleListing?.coordinates.lat || 0,
          lng: coordinates?.lon || this.bubbleListing?.coordinates.lon || 0,
        });
        this.listingBubble.setContent(content);

        // Add info bubble to the UI:
        this.ui?.addBubble(this.listingBubble);

        this.listingBubble.getElement()?.addEventListener('click', async () => {
          await this.navController.navigateForward(buildListingPointRoute(listing));
        });
      }
    }
  }

  addBoondockBubble(ev: number, coordinates?: ILatLon) {
    if (ev !== undefined) {
      const boondock = this.mapBoondockPoints?.find((el) => el.id === ev);
      if (boondock) {
        this.bubbleBoondock = new BoondockPoint(boondock);
        this.updateSelectedIcon(boondock);
        this.closeBubbles();
        this.cd.detectChanges();

        let content;
        if (this.boondockBubble?.getContentElement()) {
          content = this.boondockBubble.getContentElement();
        } else {
          content = this.bubbleBoondockContent.nativeElement.children[0];
        }

        this.boondockBubble = new H.ui.InfoBubble({
          lat: coordinates?.lat || this.bubbleBoondock?.coordinates.lat || 0,
          lng: coordinates?.lon || this.bubbleBoondock?.coordinates.lon || 0,
        });
        this.boondockBubble.setContent(content);

        // Add info bubble to the UI:
        this.ui?.addBubble(this.boondockBubble);

        this.boondockBubble.getElement()?.addEventListener('click', async () => {
          await this.navController.navigateForward(buildBoondockingPointRoute(boondock));
        });
      }
    }
  }

  addActivityBubbleById(id: number, coordinates: ILatLon) {
    if (id !== undefined) {
      const activity = this.mapPoiPoints.find((el) => el.id === id);
      if (activity) {
        this.addActivityBubble(activity, coordinates);
      }
    }
  }

  addActivityBubble(activity: IActivityLocation, coordinates: ILatLon) {
    this.currentActivity = new ActivityLocation(activity);
    this.closeBubbles();

    let content;
    if (this.activityBubble && this.activityBubble.getContentElement()) {
      content = this.activityBubble.getContentElement();
    } else {
      content = this.bubbleActivityContent.nativeElement.children[0];
    }

    this.activityBubble = new H.ui.InfoBubble({
      lat: coordinates.lat,
      lng: coordinates.lon,
    });
    this.activityBubble.setContent(content);

    // Add info bubble to the UI:
    this.ui?.addBubble(this.activityBubble);

    this.activityBubble.getElement()?.addEventListener('click', async (evt: Event) => {
      if (this.platformService.isPhoneSize()) {
        await this.navController.navigateForward(buildActivityRoute(new ActivityLocation(activity)));
      } else {
        const modal = await this.modalController.create({
          component: ActivityLocationDisplayComponent,
          componentProps: {
            activity: new ActivityLocation(activity),
          },
        });
        await modal.present();
      }
      evt.stopPropagation();
    });

    this.cd.detectChanges();
  }

  addUserPointBubbleById(id: number, coordinates?: ILatLon) {
    if (id !== undefined) {
      const userPoint = this.mapUserPoints.find((el) => el.id === id);
      if (userPoint) {
        this.addUserPointBubble(userPoint, coordinates);
      }
    }
  }

  addUserPointBubble(userPoint: IMapPoint, coordinates?: ILatLon) {
    this.currentUserPoint = userPoint;
    this.closeBubbles();

    let content;
    if (this.userPointBubble && this.userPointBubble.getContentElement()) {
      content = this.userPointBubble.getContentElement();
    } else {
      content = this.bubbleUserPointContent.nativeElement.children[0];
    }

    this.userPointBubble = new H.ui.InfoBubble({
      lat: coordinates?.lat || userPoint.coordinates.lat,
      lng: coordinates?.lon || userPoint.coordinates.lon,
    });
    this.userPointBubble.setContent(content);

    // Add info bubble to the UI:
    this.ui?.addBubble(this.userPointBubble);

    this.cd.detectChanges();
  }

  updateSelectedIcon(data?: IClusterData) {
    this.layers.poi.clusterPoints?.forEach((point, i) => {
      const markerData: IClusterData = point.data;

      if (data?.type === MapPointTypes.poi) {
        if (markerData.id === data.id) {
          setTimeout(() => {
            this.layers.poi.clusterProvider?.removeDataPoint(point);
            markerData.selected = true;
            const newPoint = new H.clustering.DataPoint(point.lat, point.lng, point.wt, markerData);
            this.layers.poi.clusterPoints?.splice(i, 1, newPoint);
            this.layers.poi.clusterProvider?.addDataPoint(newPoint);
          });
        } else if (markerData.selected) {
          setTimeout(() => {
            this.layers.poi.clusterProvider?.removeDataPoint(point);
            delete markerData.selected;
            const newPoint = new H.clustering.DataPoint(point.lat, point.lng, point.wt, markerData);
            this.layers.poi.clusterPoints?.splice(i, 1, newPoint);
            this.layers.poi.clusterProvider?.addDataPoint(newPoint);
          });
        }
      }
    });

    this.layers.boondock.clusterPoints?.forEach((point, i) => {
      const markerData: IClusterData = point.data;

      if (data?.type === MapPointTypes.boondock) {
        if (markerData.id === data.id) {
          setTimeout(() => {
            this.layers.boondock.clusterProvider?.removeDataPoint(point);
            markerData.selected = true;
            const newPoint = new H.clustering.DataPoint(point.lat, point.lng, point.wt, markerData);
            this.layers.boondock.clusterPoints?.splice(i, 1, newPoint);
            this.layers.boondock.clusterProvider?.addDataPoint(newPoint);
          });
        } else if (markerData.selected) {
          setTimeout(() => {
            this.layers.boondock.clusterProvider?.removeDataPoint(point);
            delete markerData.selected;
            const newPoint = new H.clustering.DataPoint(point.lat, point.lng, point.wt, markerData);
            this.layers.boondock.clusterPoints?.splice(i, 1, newPoint);
            this.layers.boondock.clusterProvider?.addDataPoint(newPoint);
          });
        }
      }
    });

    this.layers.host.clusterPoints?.forEach((point, i) => {
      const markerData: IClusterData = point.data;

      if (data?.type === MapPointTypes.standard) {
        if (markerData.id === data.id) {
          setTimeout(() => {
            this.layers.host.clusterProvider?.removeDataPoint(point);
            markerData.selected = true;
            const newPoint = new H.clustering.DataPoint(point.lat, point.lng, point.wt, markerData);
            this.layers.host.clusterPoints?.splice(i, 1, newPoint);
            this.layers.host.clusterProvider?.addDataPoint(newPoint);
          });
        } else if (markerData.selected) {
          setTimeout(() => {
            this.layers.host.clusterProvider?.removeDataPoint(point);
            delete markerData.selected;
            const newPoint = new H.clustering.DataPoint(point.lat, point.lng, point.wt, markerData);
            this.layers.host.clusterPoints?.splice(i, 1, newPoint);
            this.layers.host.clusterProvider?.addDataPoint(newPoint);
          });
        }
      }
    });
  }

  async handleMapTap(event: unknown) {
    await this.clearContextMenu();
    if (event instanceof H.mapevents.Event) {
      this.mapOnce$.subscribe((map) => {
        if (this.addPoiMode) {
          if ((event as H.mapevents.Event).currentPointer) {
            const coord = map.screenToGeo(
              (event as H.mapevents.Event).currentPointer.viewportX,
              (event as H.mapevents.Event).currentPointer.viewportY,
            );

            if (coord) {
              const point = new LatLon({ lat: coord.lat, lon: coord.lng });
              this.mapTapped.emit(point);
            }
          }
        } else if ((event as H.mapevents.Event).target instanceof H.Map) {
          map.removeObjects(map.getObjects());
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async handleContextMenu(event: any) {
    this.contextMenuVisible = true;
    this.cd.detectChanges();
    this.mapOnce$.subscribe((map) => {
      const contextMenu = map.screenToGeo(event.viewportX, event.viewportY);
      if (contextMenu) {
        this.contextMenuCoordinates = { lat: contextMenu.lat, lon: contextMenu.lng };
        this.cd.detectChanges();
      }

      this.contextMenuContainer.nativeElement.style.top = event.viewportY + 'px';
      this.contextMenuContainer.nativeElement.style.left = event.viewportX + 'px';
    });
  }

  async clearContextMenu() {
    if (this.contextMenuVisible) {
      this.contextMenuVisible = false;
      await this.cd.detectChanges();
    }
  }

  private setCurrentLocationPin(placeholder?: string) {
    if (this.showCurrentLocation) {
      // Set a custom icon
      const icon = placeholder ? new H.map.Icon(placeholder) : new H.map.Icon(currentLocationPin);

      const currentLocationMarker = new H.map.Marker({ lat: this.lat, lng: this.lon }, { icon, data: null });

      this.layers.currentLocation.provider?.getRootGroup()?.removeAll();

      this.layers.currentLocation.provider?.getRootGroup()?.addObject(currentLocationMarker);
    }
  }

  private dragendAddDraggable(event: Event) {
    const target = event.target;
    if (target instanceof H.map.Marker) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.dragEnd.emit(toLatLon((target as any).position));
    }
  }

  // Listen to the drag event and move the position of the marker
  // as necessary
  private dragAddDraggable(event: Event) {
    const target = event.target;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pointer = (event as any).currentPointer;
    if (target instanceof H.map.Marker) {
      this.mapOnce$.subscribe((map) => {
        if (pointer.viewportX && pointer.viewportY) {
          (target as H.map.Marker)?.setGeometry(map.screenToGeo(pointer.viewportX, pointer.viewportY) as H.geo.Point);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.drag.emit(toLatLon((target as any).position));
        }
      });
    }
  }

  /**
   * Hosted Listings Cluster Layer
   */
  private initializeHostCluster(map: H.Map) {
    this.initializeClusterLayer(
      map,
      this.layers.host,
      (id) => this.hostPhoneAction(id),
      (id) => this.hostNonPhoneAction(id),
      '227, 83, 13',
      {
        // maximum radius of the neighborhood
        eps: 16,
        // minimum weight of points required to form a cluster
        minWeight: 10000,
      },
    );
  }

  /**
   * The Boondock cluster layer.
   */
  private initializeBoondockCluster(map: H.Map) {
    this.initializeClusterLayer(
      map,
      this.layers.boondock,
      (id) => this.boondockPhoneAction(id),
      (id) => this.boondockNonPhoneAction(id),
      '70, 93, 80',
    );
  }

  /**
   * The POI cluster layer.
   */
  private initializePoiCluster(map: H.Map) {
    this.initializeClusterLayer(
      map,
      this.layers.poi,
      (id) => this.poiPhoneAction(id),
      (id, coordinates) => this.poiNonPhoneAction(id, coordinates),
      '227, 83, 13',
    );
  }

  private initializeClusterLayer(
    map: H.Map,
    plate: IOnMapLayer,
    phoneAction: (id: number) => void,
    nonPhoneAction: (id: number, coordinates: ILatLon) => void,
    color: string = '227, 83, 13',
    clusteringOptions: H.clustering.Provider.ClusteringOptions = {
      // maximum radius of the neighborhood
      eps: MAP_CLUSTER_RANGE,
      // minimum weight of points required to form a cluster
      minWeight: MAP_CLUSTER_WEIGHT,
    },
  ) {
    const circularTheme = new CircularTheme(1, color);
    const iconMarkerTheme = new IconMarkerTheme();

    if (!plate.clusterPoints) {
      plate.clusterPoints = [];
    }

    const clusterThemeFunction = (cluster: H.clustering.ICluster) => circularTheme.getClusterPresentation(cluster);

    // Create a clustering provider with custom options for clustering the input
    plate.clusterProvider = new H.clustering.Provider([], {
      clusteringOptions,
      theme: {
        getClusterPresentation: clusterThemeFunction,
        getNoisePresentation: iconMarkerTheme.generateClusterNoiseThemeFunction(
          iconMarkerTheme,
          // the next is added to add circles showing the inaccuracy of the host points
          plate.type === CurbnturfMapLayerTypes.host
            ? this.layers?.hostInaccuracy?.provider?.getRootGroup()
            : undefined,
        ),
      },
    });

    // Create a layer that will consume objects from our clustering provider
    plate.layer = new H.map.layer.ObjectLayer(plate.clusterProvider);

    const onTap = (event: any) => {
      // Log data bound to the marker that has been tapped:
      let data: IClusterData | H.clustering.ICluster = event.target?.getData();
      if ((data as IClusterData).id) {
        const coordinates: { lat: number; lng: number } = event.target.getGeometry();
        this.addMapPinAction(data as IClusterData, coordinates, event.currentPointer, phoneAction, nonPhoneAction);
      } else if ((data as H.clustering.ICluster).forEachDataPoint) {
        // remove any other markers
        map.removeObjects(map.getObjects());

        this.buildAndAddClusterPoints(
          data as H.clustering.ICluster,
          event,
          map,
          iconMarkerTheme,
          phoneAction,
          nonPhoneAction,
        );
      }
    };

    // Add an event listener to the Provider - this listener is called when a maker
    // has been tapped:
    plate?.clusterProvider?.addEventListener('tap', onTap);

    map.addLayer(plate.layer);
  }

  onCloseBubble() {
    this.closeBubbles();
  }

  private makeBoondockDataItem(boondock: IBoondockPoint): IDataItem {
    return {
      ...boondock.coordinates,
      id: boondock.id,
      selected: this.currentBoondockPoint?.id === boondock.id ? true : undefined,
    } as IDataItem;
  }

  private makeListingDataItem(listing: IListingPoint): IDataItem {
    let price = listing.price;

    return {
      ...listing.coordinates,
      price,
      id: listing.id,
      selected: this.currentPoint?.id === listing.id ? true : undefined,
      multisite: listing.availableSiteCount && listing.availableSiteCount > 1 ? true : false,
    } as IDataItem;
  }

  private boondockPhoneAction(id: number) {
    this.searchFacade.selectBoondockPointById(id);

    const listing = this.mapBoondockPoints.find((el) => el.id === id);
    if (listing) {
      this.navController.navigateForward(buildBoondockingPointRoute(listing));
    }
  }

  private boondockNonPhoneAction(id: number) {
    if (this.currentBoondockPoint && this.currentBoondockPoint.id === id) {
      this.addBoondockBubble(id);
    } else {
      this.searchFacade.selectBoondockPointById(id);
    }
  }

  private hostPhoneAction(id: number) {
    this.searchFacade.selectPointById(id);

    const listing = this.mapHostPoints.find((el) => el.id === id);
    if (listing) {
      this.navController.navigateForward(buildListingPointRoute(listing));
    }
  }

  private hostNonPhoneAction(id: number) {
    if (this.currentPoint && this.currentPoint.id === id) {
      this.addBubble(id);
    } else {
      this.searchFacade.selectPointById(id);
    }
  }

  private poiPhoneAction(id: number) {
    const poi = this.mapPoiPoints.find((el) => el.id === id);
    if (poi) {
      this.navController.navigateForward(buildActivityRoute(new ActivityLocation(poi)));
    }
  }

  private poiNonPhoneAction(id: number, coordinates: ILatLon) {
    this.addActivityBubbleById(id, coordinates);
  }

  private userPointAction(id: number, coordinates?: ILatLon) {
    this.addUserPointBubbleById(id, coordinates);
  }

  private centerIfOnEdge(currentPointer: H.mapevents.Pointer) {
    if (currentPointer.viewportY < 290 || currentPointer.viewportX < 290) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // const center = (currentPointer.target as any)?.b;
      const target = currentPointer.target;
      if (target instanceof H.map.Object) {
        const center = (target?.toGeoJSON() as Feature<Point>)?.geometry?.coordinates;
        if (center?.length >= 2) {
          this.setMapLocation(center[1], center[0], true);
        }
      }
    }
  }

  onUserPointRemoved(point: IUserCustomLocation) {
    this.closeBubbles();
    this.userPointRemoved.emit(point);
  }

  onUserPointUpdated() {
    this.closeBubbles();
  }

  private buildAndAddUserPoint(point: IMapPoint) {
    const dataItem: IDataItem = {
      id: point.id || 0,
      lat: point.coordinates.lat,
      lon: point.coordinates.lon,
      type: MapPointTypes.user,
    };
    const dataPoint = makeDataPoint(dataItem, MapPointTypes.user);

    const icon = new H.map.Icon(userPointPin, { size: { h: 28, w: 20 } });

    const marker = new H.map.Marker({ lat: dataPoint.lat, lng: dataPoint.lng }, { icon, data: dataPoint.data });

    marker.addEventListener('tap', (event: any) => {
      if (this.platformService.isPhoneSize()) {
        this.userPointAction(point.id || 0, { lat: dataPoint.lat, lon: dataPoint.lng });
      } else {
        this.centerIfOnEdge(event.currentPointer);
        this.userPointAction(point.id || 0, { lat: dataPoint.lat, lon: dataPoint.lng });
      }
    });

    this.layers.user.provider?.getRootGroup()?.addObject(marker);
  }

  private buildAndAddClusterPoints(
    data: H.clustering.ICluster,
    event: any,
    map: H.Map,
    iconMarkerTheme: IconMarkerTheme,
    phoneAction: (id: number) => void,
    nonPhoneAction: (id: number, coordinates: ILatLon) => void,
  ) {
    const origin = data.getPosition();

    const dataPointArray: IClusterData[] = [];
    data.forEachDataPoint((dataPoint) => {
      dataPointArray.push(dataPoint.getData());
    });

    dataPointArray.forEach((datum, index) => {
      if (index < 50) {
        const icon = iconMarkerTheme.getSelectedIcon(datum);
        const bottomCoordinates = multiPinCoordinates(
          map,
          origin,
          index,
          dataPointArray.length,
          (icon?.getSize()?.h || 0) / 3,
        );
        const middleCoordinates = multiPinCoordinates(map, origin, index, dataPointArray.length, 0);

        const marker = new H.map.Marker(bottomCoordinates, {
          icon,
          data: datum,
        });

        // create pin action listener
        const coordinates: { lat: number; lng: number } = { ...(marker.getGeometry() as H.geo.Point) };
        marker.addEventListener('tap', () => {
          this.addMapPinAction(datum, coordinates, event.currentPointer, phoneAction, nonPhoneAction);
        });

        const linestring = new H.geo.LineString();
        linestring.pushPoint(origin);
        linestring.pushPoint(middleCoordinates);
        const polyline = new H.map.Polyline(linestring, {
          style: { lineWidth: 3, strokeColor: 'rgba(69, 69, 69, 0.3)' },
          data: {},
        });

        map.addObjects([polyline, marker]);
      }
    });
  }

  private addMapPinAction(
    data: IClusterData,
    coordinates: { lat: number; lng: number },
    currentPointer: H.mapevents.Pointer,
    phoneAction: (id: number) => void,
    nonPhoneAction: (id: number, coordinates: ILatLon) => void,
  ) {
    this.updateSelectedIcon(data);

    if (this.platformService.isPhoneSize()) {
      phoneAction(data.id || 0);
    } else {
      this.centerIfOnEdge(currentPointer);
      nonPhoneAction(data.id || 0, { lat: coordinates.lat, lon: coordinates.lng });
    }
  }
}
